import React, { useState, useEffect } from "react";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";

// JSON Editor Modal Component
const JsonEditorModal = ({ show, onClose, item, onSave }) => {
  const [jsonData, setJsonData] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (item && item.json) {
      try {
        const parsed = JSON.parse(item.json);
        // Convert to array of key-value pairs
        const pairs = Object.entries(parsed).map(([key, value]) => ({ key, value: JSON.stringify(value) }));
        setJsonData(pairs);
        setError("");
      } catch (err) {
        setJsonData([]);
        setError("Invalid JSON format. Starting with empty object.");
      }
    } else {
      setJsonData([]);
    }
  }, [item]);

  const handleKeyChange = (index, newKey) => {
    const newData = [...jsonData];
    newData[index].key = newKey;
    setJsonData(newData);
  };

  const handleValueChange = (index, newValue) => {
    const newData = [...jsonData];
    newData[index].value = newValue;
    setJsonData(newData);
  };

  const addRow = () => {
    setJsonData([...jsonData, { key: "", value: "" }]);
  };

  const removeRow = (index) => {
    const newData = [...jsonData];
    newData.splice(index, 1);
    setJsonData(newData);
  };

  const handleSave = () => {
    try {
      // Convert array of key-value pairs back to object
      const jsonObject = {};
      
      // Check for duplicate keys
      const keys = jsonData.map(item => item.key);
      const hasDuplicates = keys.some((key, index) => 
        key && keys.indexOf(key) !== index
      );
      
      if (hasDuplicates) {
        setError("Error: Duplicate keys found. Please ensure all keys are unique.");
        return;
      }
      
      jsonData.forEach(pair => {
        if (pair.key.trim() !== "") {
          try {
            // Try to parse the value as JSON
            jsonObject[pair.key] = JSON.parse(pair.value);
          } catch (e) {
            // If parsing fails, use the raw string value
            jsonObject[pair.key] = pair.value;
          }
        }
      });
      
      // Convert to JSON string and save
      const jsonString = JSON.stringify(jsonObject, null, 2);
      onSave(jsonString);
      onClose();
    } catch (err) {
      setError(`Error saving JSON: ${err.message}`);
    }
  };



  if (!show) return null;

  return (
       <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content d-flex flex-column" style={{ maxHeight: "90vh" }}>
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              Edit JSON for {item.settingname || item.MQTTSettingname}
            </h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body" style={{ flex: '1 1 auto', overflowY: 'auto' }}>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="table-responsive">
              <table className="table table-bordered">
                 <thead className="thead-light" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                  <tr>
                    <th style={{ width: "40%" }}>Key</th>
                    <th style={{ width: "50%" }}>Value</th>
                    <th style={{ width: "10%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jsonData.map((pair, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={pair.key}
                          onChange={(e) => handleKeyChange(index, e.target.value)}
                          placeholder="Property name"
                        />
                      </td>
                      <td>
                        <textarea
                          className="form-control"
                          value={pair.value}
                          onChange={(e) => handleValueChange(index, e.target.value)}
                          placeholder="Value (string, number, object, array)"
                          rows="2"
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => removeRow(index)}
                          title="Remove row"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button className="btn btn-info btn-sm" onClick={addRow}>
              <i className="fas fa-plus"></i> Add Property
            </button>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() =>handleSave()}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const JsonViewMode = ({ show, onClose, item }) => {
  // If the modal shouldn't be shown, return null
  if (!show) return null;

  // Function to format JSON nicely
  const formatJson = (jsonString) => {
    try {
      // Parse the JSON string and then stringify it with indentation
      const parsedJson = JSON.parse(jsonString);
      return JSON.stringify(parsedJson, null, 2);
    } catch (error) {
      return "Invalid JSON format";
    }
  };

  return (
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">View JSON for {item.settingname || item.MQTTSettingname}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <pre style={{ 
              backgroundColor: "var(--surface2)", 
              padding: "15px", 
              borderRadius: "4px",
              maxHeight: "500px",
              overflow: "auto",
              color: "var(--text)"
            }}>
              {item && item.json ? formatJson(item.json) : "No JSON data available"}
            </pre>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Main MQTTSetting Component
const MQTTSetting = () => {
  const [data, setData] = useState([]);
  const [MQTTSettingId, setMQTTSettingId] = useState([]);
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [from, setFrom] = useState(0);
  const [to] = useState(0);
  const [activeStatus] = useState("");
  const [message, setMessage] = useState("");
  const [publishInfo, setPublishInfo] = useState({});
  const [copySuccess, setCopySuccess] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
 
  const [currentItem, setCurrentItem] = useState(null);
  const [copied, setCopied] = useState(false);

  const [jsonViewModalOpen, setJsonViewModalOpen] = useState(false); // Renamed for clarity
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0); // Renamed for clarity


  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token");
  const userid = getCookie("userid");

  const fetchMQTTSettingData = async () => {
    setLoading(true);
    try {
      // API endpoint
      const apiUrl = 'https://api.dqr-display-tms.bonrix.in/api/JsonSettingOperation/Search';
      
      // Headers
      const headers = {
        'accept': 'text/plain',
        'UserId': userid,
        'X-Authorization': token,
        'Content-Type': 'application/json-patch+json'
      };
      
      // Request body
      const requestBody = {
        "id": 0,
        "applycreateddate": false,
        "fromcreateddate": 0,
        "endcreateddate": 0,
        "active": "",
        "settingname": "",
        "ispageload": false,
        "pagesize": dataLimit,
        "currentpageno": count,
        "withoutpagination": false,
        "pagedirection": "first"
      };
      
      // Make the API call
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });
      
      // Parse the response
      const responseData = await response.json();
      
      // Transform the data to match the expected format in your table
      if (responseData && Array.isArray(responseData.data)) {
        const rows = responseData.data;
        const transformedData = rows.map(item => ({
          ...item,
          MQTTSettingname: item.settingname,
          publishId: item.publishid
        }));
        setData(transformedData);
        setHasMore(rows.length === Number(dataLimit));
        const approximateTotal = (count * Number(dataLimit)) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
        setTotalItems(approximateTotal);
        return transformedData;
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
        return [];
      }
    } catch (error) {
      console.error("Error fetching MQTT Setting data:", error);
      setMessage("Failed to load data. Please try again.");
      setData([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchMQTTSettingDataForId = async () => {
    try {
      const MQTTSettingGetDistinct = getUrlByOperation("MQTTSettingGetDistinct");
      const response = await fetch(`${MQTTSettingGetDistinct}/MQTTSettingname`, {
        headers: {
          accept: "text/plain",
          "X-Authorization": token,
          UserId: userid,
        },
      });

      const responseData = await response.json();
      if (responseData && Array.isArray(responseData.data)) {
        setMQTTSettingId(responseData.data);
      } else {
        setMQTTSettingId([]);
      }
    } catch (error) {
      console.error("Error fetching MQTTSetting IDs:", error);
    }
  };

  useEffect(() => {
    // Call the function
    fetchMQTTSettingData();
    fetchMQTTSettingDataForId();
  }, [getUrlByOperation, token, userid, dataLimit, count, from, to, activeStatus]);

  // Clean up message after display
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
        setCopySuccess(false);
        setCopied(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleAddPop = () => {
    navigate("/addMQTTSetting");
  };

  const handleUpdatePop = (id) => {
    navigate(`/updateMQTTSetting/${id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) {
      return;
    }
    
    try {
      const response = await fetch('https://api.dqr-display-tms.bonrix.in/api/JsonSettingOperation/Delete', {
        method: 'POST',
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify({ id }),
      });
      
      if (response.ok) {
        setMessage("Data deleted successfully!");
        setCopySuccess(true);
        fetchMQTTSettingData(); // Refresh data after delete
      } else {
        throw new Error("Failed to delete data");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      setMessage("Failed to delete data");
    }
  };

  const handleToggle = async (item) => {
    const apiUrl = item.active ? getUrlByOperation("MQTTSettingDeactive") : getUrlByOperation("MQTTSettingActive");

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify({ id: item.id }),
      });
      
      if (response.ok) {
        setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
        setMessage(`Item ${item.active ? 'deactivated' : 'activated'} successfully!`);
        setCopySuccess(true);
      } else {
        throw new Error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setMessage("Failed to update status");
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  const handleCopyHttp = (key) => {
    const urlToCopy = `http://api.dqr-display-tms.bonrix.in/api/Share/JsonSetting/Json/${key}`;
    copyToClipboard(urlToCopy)
      .then(() => {
        setCopied(true);
        setMessage("URL copied to clipboard!");
        setCopySuccess(true);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        setMessage("Failed to copy URL");
      });
  };

  const handleCopyHttps = (key) => {
    const urlToCopy = `https://api.dqr-display-tms.bonrix.in/api/Share/JsonSetting/Json/${key}`;
    copyToClipboard(urlToCopy)
      .then(() => {
        setCopied(true);
        setMessage("URL copied to clipboard!");
        setCopySuccess(true);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        setMessage("Failed to copy URL");
      });
  };

  function copyToClipboard(text) {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text)
        .then(() => true)
        .catch(err => {
          console.error('Could not copy text using Clipboard API: ', err);
          return fallbackCopyToClipboard(text);
        });
    } else {
      return Promise.resolve(fallbackCopyToClipboard(text));
    }
  }

  function fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    
    textArea.focus();
    textArea.select();
    
    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      console.error('Failed to copy with fallback method:', err);
    }
    
    document.body.removeChild(textArea);
    return success;
  }

  const handlePublish = async (id) => {
    setPublishLoading(true);
    try {
      const response = await fetch(
        'https://api.dqr-display-tms.bonrix.in/api/JsonSettingOperation/Generate/PublishId',
        {
          method: 'POST',
          headers: {
            'accept': 'text/plain',
            'UserId': userid || '1',
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          },
          body: JSON.stringify({ id })
        }
      );

      const responseData = await response.json();
      
      if (responseData && responseData.isresponse) {
        const publishId = responseData.publishid || "";
        
        const publishUrl = `${window.location.origin}/MQTTSettingpreview-publish/${publishId}`;
        
        setPublishInfo({ 
          id, 
          encodedUrl: publishUrl, 
          encodedId: publishId 
        });
        
        const copied = await copyToClipboard(publishUrl);
        
        if (copied) {
          setMessage("URL copied to clipboard!");
          setCopySuccess(true);
        } else {
          setMessage("Generated URL: " + publishUrl);
          setCopySuccess(true);
        }
        
        fetchMQTTSettingData();
      } else {
        throw new Error(responseData?.errormsg || "Failed to generate publish ID");
      }
    } catch (error) {
      console.error("Error generating publish ID:", error);
      setMessage(error.message || "Failed to generate publish URL");
    } finally {
      setPublishLoading(false);
    }
  };

  const handleCopyClick = async (id, publishId, settingtype) => {
    const publishUrl = `${window.location.origin}/CS80/${settingtype}/${publishId}`;
    
    try {
      const copied = await copyToClipboard(publishUrl);
      
      if (copied) {
        setMessage("URL copied to clipboard!");
        setCopySuccess(true);
      } else {
        setMessage("URL: " + publishUrl);
        setCopySuccess(true);
      }
    } catch (error) {
      console.error("Failed to copy URL:", error);
      setMessage("Failed to copy URL to clipboard: " + publishUrl);
    }
  };

  const handleUnPublish = async (id) => {
    try {
      const response = await fetch(
        'https://api.dqr-display-tms.bonrix.in/api/JsonSettingOperation/UnPublishId',
        {
          method: 'POST',
          headers: {
            'accept': 'text/plain',
            'UserId': userid || '1',
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          },
          body: JSON.stringify({ id })
        }
      );
      
      const responseData = await response.json();
      
      if (responseData && responseData.isresponse) {

        setCopySuccess(true);
        fetchMQTTSettingData();
      } else {
        throw new Error(responseData?.errormsg || "Failed to unpublish setting");
      }
    } catch (error) {
      console.error("Error unpublishing setting:", error);
      setMessage(error.message || "Failed to unpublish setting");
    }
  };

  // Function to handle opening the JSON editor modal
  const handleEditJson = (item) => {
    setCurrentItem(item);
    setJsonModalOpen(true);
  };
 
   const handleViewPOP = (item) => {
    setCurrentItem(item);
    setJsonViewModalOpen(true); // Use the setter function, not the state variable
  };
  

  // Function to handle saving JSON from the modal
  const handleSaveJson = async (jsonString) => {
    try {
      // Parse to validate
      JSON.parse(jsonString);
      
      // Prepare the update payload
      const updatePayload = {
        id: currentItem.id,
        active: currentItem.active,
        remark: currentItem.remark,
        settingname: currentItem.settingname || currentItem.MQTTSettingname,
        settingtype: currentItem.settingtype || "default",
        json: jsonString
      };
      
      // Send update request
      const response = await fetch('https://api.dqr-display-tms.bonrix.in/api/JsonSettingOperation/Update', {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'UserId': userid || '1',
          'X-Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log("Update successful:", result);
        
        setMessage("JSON updated successfully!");
        setCopySuccess(true);
        
        // Update the data in the state
        setData(data.map(item => 
          item.id === currentItem.id 
            ? { ...item, json: jsonString } 
            : item
        ));
      } else {
        throw new Error("Update failed: " + (await response.text()));
      }
    } catch (error) {
      console.error("Error updating JSON:", error);
      setMessage("Failed to update JSON: " + error.message);
    }
  };

  // Function to render the publish column
  const renderPublishColumn = (item) => {
    // If the item already has a publishid, show it with a copy button
    if (item.publishid) {
      // Truncate publishid if it's longer than 5 characters
      const displayId = item.publishid.length > 5 
        ? `${item.publishid.substring(0, 5)}...` 
        : item.publishid;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <div className="btn-group">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleCopyClick(item.id, item.publishid, item.settingtype)}
              title="Copy URL"
            >
              <i className="fas fa-copy mr-1"></i> URL
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttp(item.publishid)}
              title="Copy HTTP URL"
            >
              HTTP
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttps(item.publishid)}
              title="Copy HTTPS URL"
            >
              HTTPS
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleUnPublish(item.id, item.publishid)}
              title="Unpublish"
            >
              <i className="fas fa-eye-slash"></i>
            </button>
          </div>
        </div>
      );
    }
    
    // Check if this item is currently being processed
    if (publishInfo.id === item.id) {
      // Truncate encodedId if it's longer than 5 characters
      const displayId = publishInfo.encodedId.length > 5 
        ? `${publishInfo.encodedId.substring(0, 5)}...` 
        : publishInfo.encodedId;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleCopyClick(item.id, publishInfo.encodedId)}
            title="Copy URL"
          >
            <i className="fas fa-copy mr-1"></i> Copy URL
          </button>
        </div>
      );
    }
    
    // If no publishid and not currently processing, show the publish button
    return (
      <button
        className="btn btn-sm btn-success"
        onClick={() => handlePublish(item.id)}
        disabled={publishLoading}
      >
        {publishLoading ? (
          <>
            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
            Publishing...
          </>
        ) : (
          <>
            <i className="fas fa-globe mr-1"></i> Publish
          </>
        )}
      </button>
    );
  };

  // Function to render the JSON display column
  const renderJsonColumn = (item) => {
    return (
      <div className="json-preview">
        <div className="mb-2">
          {item.json ? (
            <div 
              className="text-truncate" 
              style={{ 
                maxWidth: '150px', 
                maxHeight: '50px', 
                overflow: 'hidden',
                padding: '4px',
                backgroundColor: 'var(--surface2)',
                borderRadius: '4px',
                border: '1px solid var(--border)',
                fontSize: '0.8rem',
                color: 'var(--text)'
              }}
            >
              {item.json}
            </div>
          ) : (
            <span className="text-muted">Empty JSON</span>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-primary ml-3"
          onClick={() => handleEditJson(item)}
        >
          <i className="fas fa-edit "></i> 
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary ml-1"
          onClick={() => handleViewPOP(item)}
        >
          <i className="fas fa-eye "></i>
        </button>
      </div>
    );
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h1 className="mb-0">Json Settings</h1>
                    <button type="button" className="btn btn-primary" onClick={handleAddPop}>
                      <i className="fas fa-plus mr-1"></i> Add MQTT Setting
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Json Settings List</h3>
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <p className="mt-2">Loading settings...</p>
                    </div>
                  ) : data.length === 0 ? (
                    <div className="text-center py-5">
                      <p>No MQTT settings found.</p>
                      <button className="btn btn-primary" onClick={handleAddPop}>
                        <i className="fas fa-plus mr-1"></i> Add Json Setting
                      </button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-striped">
                         <thead className="thead-light" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                          <tr>
                            <th>ID</th>
                            <th>Setting Name</th>
                            <th>Setting Type</th>
                            <th>Remark</th>
                            <th>Status</th>
                            <th>JSON</th>
                            <th>Publish</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((item, index) => (
                            <tr key={index}>
                              <td>{item.id}</td>
                              <td>{item.MQTTSettingname || item.settingname}</td>
                              <td>{item.settingtype}</td>
                              <td>{item.remark || "-"}</td>
                              <td>
                                <div className="custom-control custom-switch">
                                  <Switch
                                    isOn={item.active}
                                    handleToggle={() => handleToggle(item)}
                                    id={`switch-${item.id}`}
                                  />
                                  {/* <span className="ml-2">{item.active ? "Active" : "Inactive"}</span> */}
                                </div>
                              </td>
                              <td>{renderJsonColumn(item)}</td>
                              <td>{renderPublishColumn(item)}</td>
                              <td>
                                <div className="btn-group">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-info"
                                    onClick={() => handleUpdatePop(item.id)}
                                    title="Update"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(item.id)}
                                    title="Delete"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <Pagination
                        currentPage={count + 1}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={dataLimit}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalItems}
                        showPageNumbers={false}
                        showItemsPerPageSelector={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* JSON Editor Modal */}
      <JsonEditorModal
        show={jsonModalOpen}
        onClose={() => setJsonModalOpen(false)}
        item={currentItem || {}}
        onSave={handleSaveJson}
      />
      
      {/* JSON View Modal - Fixed component usage */}
      <JsonViewMode
        show={jsonViewModalOpen}
        onClose={() => setJsonViewModalOpen(false)}
        item={currentItem || {}}
      />

      {/* Toast notifications */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "20px", zIndex: "9999" }}>
          <div
            className={`toast show ${copySuccess ? "bg-success" : "bg-danger"}`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="toast-header">
              {/* <strong className="mr-auto">{copySuccess ? " URL copied to clipboard!" : "Error"}</strong> */}
              <button 
                type="button" 
                className="ml-2 mb-1 close" 
                onClick={() => setMessage("")}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            {/* <div className="toast-body text-white">
              {message}
            </div> */}
          </div>
        </div>
      )}
      
      {/* Keep the original message components for compatibility */}
      {message === "URL copied to clipboard!" && <SuccessMessage message={message} />}
      {message === "Data deleted successfully!" && <SuccessMessage message={message} />}
      {message === "Failed to delete data" && <FailedMessage message={message} />}
    </>
  );
};

export default MQTTSetting;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}